import { AiSparkle } from '@/components/AiSurface';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { featureFlags } from '@/featureFlags';
import { useCreateMenuFromSources } from '@/features/menu/hooks/useCreateMenuFromSources';
import { buildMenuSources, type MenuSourceFile } from '@/features/menu/utils/menu-sources';
import { uploadAttachment } from '@/features/ai-assistant/api/assistant-api';
import { VoiceInputButton } from '@/features/voice-input/components/VoiceInputButton';
import { resolveErrorText } from '@/api/errors';
import { getErrorT, useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { sectionRouter } from '@/utils/navigateMain';
import {
  AI_MENU_SOURCES_MAX,
  AiMenuSourceSchema,
  CreateMenuFromSourcesSchema,
} from '@restaurant/api-contracts';
import {
  Block,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useRef, useState } from 'react';

const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];

const inputValue = (e: { target: EventTarget | null }) =>
  e.target instanceof HTMLInputElement ? e.target.value : '';

function showError(error: unknown) {
  f7.toast.show({ text: resolveErrorText(error, getErrorT()), closeTimeout: 4000 });
}

function SourceTile({
  file,
  progress,
  onRemove,
}: {
  file: MenuSourceFile;
  progress: number;
  onRemove: () => void;
}) {
  const c = useT().menuCreate;
  return (
    <div className="ai-create__tile" data-testid={`menu-create-source-${file.localId}`}>
      {file.kind === 'photo' && file.previewUrl ? (
        <img src={file.previewUrl} alt={file.fileName} />
      ) : (
        <span className="ai-create__tile-file">
          <i className="f7-icons" aria-hidden="true">
            doc_text
          </i>
          <span>{file.fileName}</span>
        </span>
      )}
      {file.attachmentId === null ? (
        <span className="ai-create__tile-busy">
          <Preloader size={20} />
          <span
            className="ai-create__tile-progress"
            data-testid={`menu-create-source-progress-${file.localId}`}
          >
            {Math.round(progress * 100)}%
          </span>
        </span>
      ) : (
        <button
          type="button"
          className="ai-create__tile-remove"
          onClick={onRemove}
          aria-label={c.removeSource}
          data-testid={`menu-create-source-remove-${file.localId}`}
        >
          <i className="f7-icons" aria-hidden="true">
            xmark_circle_fill
          </i>
        </button>
      )}
    </div>
  );
}

export default function CreateMenuWithAiPage() {
  const t = useT();
  const c = t.menuCreate;
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const create = useCreateMenuFromSources();
  const [name, setName] = useState('');
  const [files, setFiles] = useState<MenuSourceFile[]>([]);
  const [link, setLink] = useState('');
  const [hint, setHint] = useState('');
  const [keepItemNumbers, setKeepItemNumbers] = useState(true);
  const [rejected, setRejected] = useState<string[]>([]);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const fileInput = useRef<HTMLInputElement | null>(null);
  const cameraInput = useRef<HTMLInputElement | null>(null);
  const nextId = useRef(0);

  const addFiles = async (picked: File[]) => {
    const room = AI_MENU_SOURCES_MAX - files.length - (link.trim() ? 1 : 0);
    const accepted = picked.filter((file) => ACCEPTED.includes(file.type));
    setRejected(picked.filter((file) => !ACCEPTED.includes(file.type)).map((file) => file.name));
    if (accepted.length > room) {
      f7.toast.show({ text: c.tooManySources(AI_MENU_SOURCES_MAX), closeTimeout: 3000 });
    }
    const queued = accepted.slice(0, Math.max(room, 0)).map((file) => {
      nextId.current += 1;
      const photo = file.type !== 'application/pdf';
      const entry: MenuSourceFile = {
        localId: `f${nextId.current}`,
        kind: photo ? 'photo' : 'pdf',
        fileName: file.name,
        previewUrl: photo ? URL.createObjectURL(file) : null,
        attachmentId: null,
      };
      return { file, entry };
    });
    if (queued.length === 0 || !restaurantId) return;
    setFiles((current) => [...current, ...queued.map((q) => q.entry)]);
    await Promise.all(
      queued.map(async ({ file, entry }) => {
        try {
          const uploaded = await uploadAttachment(restaurantId, file, (fraction) =>
            setProgress((current) => ({ ...current, [entry.localId]: fraction })),
          );
          setFiles((current) =>
            current.map((f) =>
              f.localId === entry.localId ? { ...f, attachmentId: uploaded.id } : f,
            ),
          );
        } catch (error) {
          setFiles((current) => current.filter((f) => f.localId !== entry.localId));
          showError(error);
        }
      }),
    );
  };

  const removeFile = (localId: string) =>
    setFiles((current) => current.filter((f) => f.localId !== localId));

  const trimmedLink = link.trim();
  const linkInvalid =
    trimmedLink.length > 0 &&
    !AiMenuSourceSchema.safeParse({ kind: 'url', url: trimmedLink }).success;
  const uploading = files.some((f) => f.attachmentId === null);
  const body = {
    name: name.trim() || undefined,
    hint: hint.trim() || undefined,
    keepItemNumbers,
    sources: buildMenuSources(files, trimmedLink),
  };
  const valid = CreateMenuFromSourcesSchema.safeParse(body).success;
  const canCreate = valid && !uploading && !create.isPending;

  const submit = () => {
    if (!canCreate) return;
    create.mutate(body, {
      onSuccess: ({ menuId }) =>
        sectionRouter()?.navigate(`/menu/${menuId}`, { reloadCurrent: true }),
      onError: showError,
    });
  };

  const pick = (input: HTMLInputElement | null) => input?.click();

  return (
    <Page name="menu-create-ai">
      <Navbar title={c.title} backLink={t.back} />
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListInput
          label={c.nameLabel}
          type="text"
          placeholder={c.namePlaceholder}
          value={name}
          onInput={(e) => setName(inputValue(e))}
          data-testid="menu-create-name"
        />
      </List>

      <BlockTitle>{c.sources}</BlockTitle>
      <Block className="ai-create__sources-block">
        <div className="ai-create__sources" data-testid="menu-create-sources">
          {files.map((file) => (
            <SourceTile
              key={file.localId}
              file={file}
              progress={progress[file.localId] ?? 0}
              onRemove={() => removeFile(file.localId)}
            />
          ))}
          <button
            type="button"
            className="ai-create__tile ai-create__tile--add"
            onClick={() => pick(cameraInput.current)}
            data-testid="menu-create-camera"
          >
            <i className="f7-icons" aria-hidden="true">
              camera
            </i>
            <span>{c.camera}</span>
          </button>
          <button
            type="button"
            className="ai-create__tile ai-create__tile--add"
            onClick={() => pick(fileInput.current)}
            aria-label={c.addFiles}
            data-testid="menu-create-add-files"
          >
            <i className="f7-icons" aria-hidden="true">
              plus
            </i>
            <span>{c.addFiles}</span>
          </button>
        </div>
        {rejected.length > 0 && (
          <p className="ai-create__error" role="alert" data-testid="menu-create-rejected">
            {c.unsupportedFiles(rejected.join(', '))}
          </p>
        )}
        <p className="ai-create__help">{c.sourcesHelp}</p>
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          data-testid="menu-create-camera-input"
          onChange={(e) => {
            void addFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPTED.join(',')}
          multiple
          hidden
          data-testid="menu-create-file-input"
          onChange={(e) => {
            void addFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
      </Block>

      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListInput
          label={c.linkLabel}
          type="url"
          inputmode="url"
          placeholder={c.linkPlaceholder}
          value={link}
          onInput={(e) => setLink(inputValue(e))}
          errorMessage={c.invalidLink}
          errorMessageForce={linkInvalid}
          data-testid="menu-create-link"
        />
      </List>

      <BlockTitle>{c.hintLabel}</BlockTitle>
      <Block className="ai-create__hint-block">
        <div className="ai-create__hint">
          <textarea
            rows={3}
            value={hint}
            placeholder={c.hintPlaceholder}
            onChange={(e) => setHint(e.target.value)}
            data-testid="menu-create-hint"
          />
          {featureFlags.voiceInput && (
            <VoiceInputButton value={hint} onChange={setHint} data-testid="menu-create-voice" />
          )}
        </div>
      </Block>

      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={c.keepItemNumbers}>
          <ToggleSwitch
            slot="after"
            checked={keepItemNumbers}
            onChange={setKeepItemNumbers}
            data-testid="menu-create-keep-numbers"
          />
        </ListItem>
      </List>

      <Block className="ai-create__footer">
        <p>{c.offNote}</p>
        <Button
          fill
          round
          large
          disabled={!canCreate}
          onClick={submit}
          data-testid="menu-create-submit"
        >
          {create.isPending ? <Preloader size={20} color="white" /> : <AiSparkle size={18} />}
          <span>{c.create}</span>
        </Button>
      </Block>
    </Page>
  );
}
